import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Button, Dropdown } from "../ui";
import { visibleGroups } from "./navigation";

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `block rounded-md px-3 py-2 text-sm transition-colors ${
    isActive ? "bg-brand/15 text-brand" : "text-ink-muted hover:bg-line/25 hover:text-ink"
  }`;

/**
 * Shell do Builder: sidebar fixa no desktop, gaveta no mobile.
 * Único layout das telas autenticadas — não criar outro.
 */
export default function BuilderLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groups = visibleGroups(user?.role);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const nav = (
    <nav className="space-y-6">
      {groups.map((group) => (
        <div key={group.title}>
          <p className="px-3 pb-2 font-mono text-[11px] uppercase tracking-wider text-ink-muted/70">
            {group.title}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={linkClass}
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-full">
      {/* Sidebar desktop/tablet */}
      <aside className="hidden w-60 shrink-0 border-r border-line/50 bg-surface-raised/30 px-3 py-5 md:block">
        <Link to="/dashboard" className="mb-6 flex items-center gap-2 px-3">
          <span className="grid size-7 place-items-center rounded-md bg-brand font-mono text-sm font-bold text-brand-ink">
            O
          </span>
          <span className="text-sm font-semibold tracking-wide">OKTON BUILDER</span>
        </Link>
        {nav}
      </aside>

      {/* Gaveta mobile */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <aside className="relative h-full w-64 border-r border-line/50 bg-surface-raised px-3 py-5">
            {nav}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-line/50 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menu"
            >
              ☰
            </Button>
            <span className="text-sm font-semibold md:hidden">Okton Builder</span>
          </div>

          <div className="flex items-center gap-3">
            {user && <Badge tone="info">{user.role_label}</Badge>}
            <Dropdown
              trigger={
                <button className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-ink-muted hover:text-ink">
                  <span className="grid size-7 place-items-center rounded-full border border-line/70 font-mono text-xs">
                    {user?.name?.charAt(0).toUpperCase() ?? "?"}
                  </span>
                  <span className="hidden sm:inline">{user?.name}</span>
                </button>
              }
              items={[
                { label: "Perfil", onSelect: () => navigate("/profile") },
                { label: "Configurações", onSelect: () => navigate("/settings") },
                { label: "Sair", destructive: true, onSelect: () => void handleLogout() },
              ]}
            />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
          <div className="mx-auto w-full max-w-6xl space-y-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
